import { useCallback, useState } from 'react'
import type { FocusEvent, MouseEvent } from 'react'

/**
 * Anchor state for `@hivewatch/dark`'s `Tooltip`, which positions itself from an
 * element rather than wrapping its trigger.
 *
 * Works on any element, not just a focusable control — attach the handlers to a
 * wrapper when the trigger itself cannot receive mouse events (a `disabled`
 * button, for instance).
 *
 * Promoted from `features/incident-rules-table` once a second feature needed it,
 * per .claude/rules/hooks.md.
 */
export const useTooltipAnchor = <T extends HTMLElement = HTMLElement>() => {
  const [anchorEl, setAnchorEl] = useState<T | null>(null)

  const onMouseEnter = useCallback((event: MouseEvent<T>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const onMouseLeave = useCallback(() => {
    setAnchorEl(null)
  }, [])

  // Focus twins of the mouse handlers so a keyboard user reaching the anchor
  // (give the wrapper tabIndex={0}) gets the same tooltip a hover shows.
  const onFocus = useCallback((event: FocusEvent<T>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const onBlur = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return {
    anchorEl,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
  }
}
